'use client';

import React, { useEffect, useRef, useState } from 'react';
import {
  CTA_BETA_URL,
  CTA_CONTEXT_DEK,
  CTA_NEWSLETTER,
  CTA_TREATMENTS,
  hashString,
  resolveStackTerm,
  type CtaContext,
  type CtaTreatment,
  type CtaVariant,
} from '@/lib/cta';
import {
  trackBetaSignupClick,
  trackBetaSignupClickThenNavigate,
  trackCtaViewed,
  trackNewsletterError,
  trackNewsletterSubmit,
  trackNewsletterSuccess,
} from '@/lib/analytics';
import { capture as capturePH } from '@/lib/posthog';
import { qrMatrix, CTA_QR_URL } from '@/lib/qr';

/**
 * ResearchCTA — the reusable "turn research into an evidence-based stack"
 * module (beta-signup era). Ported pixel-for-pixel from the design handoff
 * (`CTA design/.../cta.js` + `cta.css`; styles live in `styles/cta.css`).
 *
 * Client component, but Next.js server-renders the full markup into the
 * initial HTML — every prop is resolved deterministically upstream
 * (lib/cta.ts), so a given URL serves identical bytes to users and
 * crawlers. Do NOT introduce per-visitor branching here.
 */

export interface ResearchCTAProps {
  context?: CtaContext;
  topicName?: string;
  variant?: CtaVariant;
  treatment?: CtaTreatment;
  stackTerm?: string;
  stackTerms?: string[];
  termIndex?: number;
  termSeed?: string;
  marker?: string;
  className?: string;
}

type NewsState = 'default' | 'loading' | 'success' | 'error';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function ResearchCTA({
  context = 'generic',
  topicName = '',
  variant = 'sidebar',
  treatment = 'stack',
  stackTerm,
  stackTerms,
  termIndex,
  termSeed,
  marker = '',
  className,
}: ResearchCTAProps) {
  const tr = CTA_TREATMENTS[treatment] ?? CTA_TREATMENTS.stack;
  const dark = variant === 'endcap';
  const term = resolveStackTerm({ stackTerm, stackTerms, termIndex, termSeed: termSeed || topicName });

  // `rail` is the narrow (~200px) sticky-TOC variant — treated as compact for
  // copy, but button-only: no newsletter field / QR / badges (too tight).
  const isRail = variant === 'rail';
  const compact = variant === 'sidebar' || variant === 'compact' || isRail;
  const dek = compact && tr.dekShort
    ? tr.dekShort
    : tr.dek
      ? tr.dek(topicName, marker)
      : (CTA_CONTEXT_DEK[context] ?? CTA_CONTEXT_DEK.generic)(topicName || 'your goals');
  const headline = (compact && tr.headlineShort ? tr.headlineShort : tr.headline)(term, marker);
  // The waitlist "hero" newsletter reads inline-size in the sidebar rail.
  const newsletterMode = variant === 'sidebar' && tr.newsletter === 'hero' ? 'inline' : tr.newsletter;

  const arm = { treatment, context, variant };

  // Distinct accessible names so a page's multiple complementary landmarks
  // (sidebar/rail + inline/early + endcap) are distinguishable in a screen
  // reader's landmark menu. On the article page the visible CTAs always carry
  // different variants, so a per-variant suffix de-duplicates them.
  const ctaLabel =
    variant === 'endcap'
      ? 'Join the Aviado beta (end of article)'
      : variant === 'rail'
        ? 'Join the Aviado beta (sidebar)'
        : 'Join the Aviado beta';

  const aside = isRail
    ? null
    : tr.showQR ? (
        <BetaQr />
      ) : newsletterMode === 'inline' || newsletterMode === 'hero' ? (
        <NewsletterField mode={newsletterMode} arm={arm} />
      ) : null;

  const needsRule = (variant === 'sidebar' || variant === 'compact') && aside;

  return (
    <NewsStateRoot
      className={`rcta rcta--${variant} rcta--t-${treatment}${dark ? ' rcta--dark' : ''}${className ? ` ${className}` : ''}`}
      ariaLabel={ctaLabel}
      treatment={treatment}
      arm={arm}
    >
      <div className="rcta__pitch">
        <div className="rcta__kicker">
          <SparkIcon />
          <span>{tr.kicker}</span>
          <span className="rcta__beta">Beta</span>
        </div>
        <h3 className="rcta__headline">
          {headline.pre}
          <em>{headline.emphasis}</em>
          {headline.post}
        </h3>
        <p className="rcta__dek">{dek}</p>
        <div className="rcta__actions">
          <a
            href={CTA_BETA_URL}
            className="rcta__btn"
            onClick={(e) => {
              capturePH('beta_signup_clicked', arm);
              // Modified/middle clicks open a new tab/window — this page does
              // not unload, so the GA4 beacon sends fine. Let the browser
              // handle navigation; just fire the event.
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
                trackBetaSignupClick(arm);
                return;
              }
              // Plain same-tab click navigates cross-page (→ /get-app) and can
              // drop the GA4 hit on unload. Fire, then navigate on gtag's
              // callback (300ms fallback) so the conversion reliably records.
              e.preventDefault();
              trackBetaSignupClickThenNavigate(arm, CTA_BETA_URL);
            }}
          >
            {tr.button} <ArrowIcon />
          </a>
          <span className="rcta__webnote">Web app live · iOS &amp; Android soon</span>
        </div>
        {tr.showBadges && <AppBadges />}
      </div>
      {needsRule ? <hr className="rcta__rule" /> : null}
      {aside}
    </NewsStateRoot>
  );
}

// ---------------------------------------------------------------------------
// Newsletter state machine — default → loading → success | error.
// State lives on the root via data-news so the ported cta.css selectors
// (`.rcta[data-news="..."]`) drive visibility, same as the prototype.
// ---------------------------------------------------------------------------

const NewsStateContext = React.createContext<{
  state: NewsState;
  setState: (s: NewsState) => void;
}>({ state: 'default', setState: () => {} });

function NewsStateRoot({
  className,
  ariaLabel,
  treatment,
  arm,
  children,
}: {
  className: string;
  ariaLabel: string;
  treatment: CtaTreatment;
  arm: { treatment: CtaTreatment; context: CtaContext; variant: CtaVariant };
  children: React.ReactNode;
}) {
  const [state, setState] = useState<NewsState>('default');
  const ref = useRef<HTMLElement>(null);

  // Impression — fire `cta_viewed` once when ≥50% of the CTA is on screen.
  // This is the denominator for "which CTA converts" (impression → click rate
  // per treatment). IntersectionObserver so off-screen endcaps don't inflate
  // the count with views that never happened.
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    let fired = false;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !fired) {
            fired = true;
            capturePH('cta_viewed', arm);
            trackCtaViewed(arm);
            io.disconnect();
          }
        }
      },
      { threshold: 0.5 }
    );
    io.observe(el);
    return () => io.disconnect();
    // arm is stable per render (derived from props); deps intentionally empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <NewsStateContext.Provider value={{ state, setState }}>
      <aside
        ref={ref}
        className={className}
        data-news={state}
        data-treatment={treatment}
        aria-label={ariaLabel}
      >
        {children}
      </aside>
    </NewsStateContext.Provider>
  );
}

function NewsletterField({
  mode,
  arm,
}: {
  mode: 'inline' | 'hero';
  arm: { treatment: CtaTreatment; context: CtaContext; variant: CtaVariant };
}) {
  const { state, setState } = React.useContext(NewsStateContext);
  const [email, setEmail] = useState('');
  const n = CTA_NEWSLETTER;

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const valid = EMAIL_RE.test(email.trim());
    trackNewsletterSubmit({ ...arm, valid });
    capturePH('newsletter_submitted', { ...arm, valid });
    if (!valid) {
      setState('error');
      return;
    }
    setState('loading');
    try {
      const res = await fetch('/research/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (!res.ok) throw new Error(`subscribe failed: ${res.status}`);
      setState('success');
      trackNewsletterSuccess({ ...arm, list: 'longevity_daily' });
      capturePH('newsletter_succeeded', { ...arm, list: 'longevity_daily' });
    } catch {
      setState('error');
      trackNewsletterError({ ...arm, reason: 'network' });
      capturePH('newsletter_failed', { ...arm, reason: 'network' });
    }
  }

  return (
    <div className={`rcta__news rcta__news--${mode}`}>
      <p className="rcta__news-label">
        Get <b>Aviado Longevity Daily</b>
        {mode === 'hero'
          ? ' — the most important evidence-backed longevity news in your inbox, daily and free.'
          : ' — the most important evidence-backed longevity news in your inbox, plus a spot in the beta line.'}
      </p>
      <form className="rcta__form" noValidate onSubmit={handleSubmit}>
        <input
          className="rcta__input"
          type="email"
          required
          placeholder={n.placeholder}
          aria-label="Email address"
          autoComplete="email"
          value={email}
          disabled={state === 'loading'}
          onChange={(e) => {
            setEmail(e.target.value);
            if (state === 'error') setState('default');
          }}
        />
        <button type="submit" className="rcta__news-btn" disabled={state === 'loading'}>
          <span className="rcta__spin" aria-hidden="true" />
          <span className="rcta__news-btn-label">{mode === 'hero' ? n.buttonHero : n.button}</span>
        </button>
      </form>
      <p className="rcta__privacy">
        By subscribing you agree to Aviado&apos;s{' '}
        <a href="https://www.aviado.com/app/privacy-policy" rel="noopener">Privacy Policy</a>.
      </p>
      <p className="rcta__msg rcta__msg--ok">
        <CheckIcon />
        <span>{n.success}</span>
      </p>
      <p className="rcta__msg rcta__msg--err">
        <AlertIcon />
        <span>{n.error}</span>
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Real QR — encodes CTA_QR_URL (the beta page, utm-tagged for cross-device
// scan attribution). qrMatrix() is synchronous + pure, so the rendered SVG
// is byte-identical for every visitor and for Googlebot (SSR/no-cloaking
// safe). 1-module quiet-zone border is added so scanners lock on.
// ---------------------------------------------------------------------------

function BetaQr() {
  const { size, cells } = qrMatrix(CTA_QR_URL);
  const quiet = 2; // modules of white margin on each side (spec minimum is 4;
  // the card's own white padding supplies the rest)
  const dim = size + quiet * 2;

  return (
    <div className="rcta__qr">
      <svg
        className="rcta__qr-svg"
        viewBox={`0 0 ${dim} ${dim}`}
        shapeRendering="crispEdges"
        role="img"
        aria-label="QR code linking to the Aviado beta signup"
      >
        <rect width={dim} height={dim} fill="#fff" />
        <g fill="#0C1222">
          {cells.map(({ x, y }) => (
            <rect key={`${x}-${y}`} x={x + quiet} y={y + quiet} width={1} height={1} />
          ))}
        </g>
      </svg>
      <div className="rcta__qr-cap">Scan to join the beta</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Icons (inline SVG, per prototype)
// ---------------------------------------------------------------------------

function SparkIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
      <circle cx={12} cy={12} r={9} />
      <path d="M12 8v5M12 16h.01" />
    </svg>
  );
}

function AppBadges() {
  return (
    <div className="rcta__badges" aria-label="Mobile apps coming soon">
      <span className="rcta__badge">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M16.5 1.5c.1 1-.3 2-1 2.8-.7.8-1.8 1.4-2.8 1.3-.1-1 .4-2 1-2.7.8-.8 2-1.4 2.8-1.4zM19 17.3c-.5 1.2-.8 1.7-1.4 2.7-.9 1.4-2.1 3.1-3.6 3.1-1.3 0-1.7-.8-3.4-.8s-2.2.8-3.4.8c-1.5 0-2.7-1.6-3.6-3C1 19.6.2 16 1.6 13.4c.8-1.4 2.2-2.3 3.7-2.3 1.4 0 2.3.9 3.4.9 1.1 0 1.8-.9 3.4-.9 1.3 0 2.7.7 3.6 2-3.1 1.7-2.6 6.1.7 7.2z" />
        </svg>
        iOS
      </span>
      <span className="rcta__badge">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M3.6 2.2l10.2 9.8L3.6 21.8c-.3-.2-.5-.6-.5-1V3.2c0-.4.2-.8.5-1zM15.3 13.4l2.6 2.5-9.6 5.5 7-8zM18.9 9.9l2.4 1.4c.6.4.6 1.4 0 1.8l-2.5 1.4-2.8-2.7 2.9-1.9zM5.6 2l9.7 5.6-2.6 2.5L5.6 2z" />
        </svg>
        Android
      </span>
      <span className="rcta__badge-note">coming soon</span>
    </div>
  );
}

// Re-export for convenient prop computation at call sites.
export { hashString };
export default ResearchCTA;
